import "./myevents.css";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { handleError } from "../../helpers/handleError";
import { getMyEventsApi } from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import boleto from "../../assets/image/boleto-2 1-white.svg";
import ubi from "../../assets/image/ubicacion 1-white.svg";
import calendario from "../../assets/image/calendario 1-white.svg";
import cancelacion from "../../assets/image/cancelacion 1.svg";

const MyEvents = () => {
  const [events, setEvents] = useState([]);
  const token = useSelector((state) => state.auth.accessToken);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("todos");
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 640px)" });

  const fetchMyEvents = async () => {
    try {
      setLoading(true);
      const res = await getMyEventsApi(token);
      if (res.status === 200) {
        setEvents(res.data.info || []);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMyEvents();
  }, [token]);

  const withStatus = useMemo(() => {
    const now = dayjs();
    const nextWeek = now.add(7, "day");
    return (events || []).map((ev) => {
      const start = dayjs(String(ev.startDate || ev.date || ev.start || '').slice(0,10));
      const isPast = start.isValid() ? start.isBefore(now, "day") : false;

      const ticketsPurchased = Number(ev.ticketsPurchased || ev.totalTickets || ev.count || 0);
      const redeemedCandidates = [
        ev.redeemedTickets,
        ev.ticketsRedeemed,
        ev.ticketsUsed,
        ev.redeemed,
      ].filter((v) => typeof v !== "undefined");
      let redeemed = 0;
      if (redeemedCandidates.length) redeemed = Number(redeemedCandidates[0] || 0);
      if (Array.isArray(ev.tickets)) {
        try {
          redeemed = ev.tickets.filter((t) => (t.status || t.state) === "redeemed" || t.redeemed === true).length;
        } catch {}
      }

      let status = "vigente";
      if (isPast) status = "expirado";
      if (ticketsPurchased > 0 && redeemed >= ticketsPurchased) status = "canjeado";

      // Flag de proximidad: evento dentro de los próximos 7 días
      const isSoon = start.isValid() && start.isAfter(now) && start.isBefore(nextWeek);

      return { ...ev, _status: status, _start: start, _purchased: ticketsPurchased, _redeemed: redeemed, _isSoon: isSoon };
    });
  }, [events]);

  const filtered = useMemo(() => {
    if (tab === "todos") return withStatus;
    return withStatus.filter((e) => e._status === tab);
  }, [withStatus, tab]);

  // El badge "¡Próximo evento!" se mostrará en todos los eventos
  // que ocurran dentro de los próximos 7 días.

  if (loading) return <SpinLoading />;

  return (
    <>
      <div className="myevents-wrapper pading150">
        <div className="myevents-header">
          <h1 className="myevents-title">Mis eventos</h1>
          <div className="myevents-tabs" role="tablist" aria-label="Filtros de eventos">
            {[
              { id: "todos", label: "Todos" },
              { id: "vigente", label: "Vigentes" },
              { id: "canjeado", label: "Canjeados" },
              { id: "expirado", label: "Expirados" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                className={`myevents-tab ${tab === t.id ? "is-active" : ""}`}
                onClick={() => setTab(t.id)}
                role="tab"
                aria-selected={tab === t.id}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="myevents-empty" role="status" aria-live="polite">
            <img src={cancelacion} alt="Icono de evento vacío" className="myevents-empty-icon" />
            <div className="myevents-empty-text">Todavía no compraste entradas</div>
          </div>
        ) : (
          <div className="myevents-grid">
            {filtered.map((item, idx) => {
              const id = item.eventid || item.id;
              const isSoon = item._isSoon;
              const dateStr = (() => {
                const d = dayjs(String(item.startDate || item.date || item.start || '').slice(0,10));
                return d.isValid() ? d.format("D MMM. • YYYY") : "";
              })();
              const entries = Number(item.ticketsPurchased || item.totalTickets || 0);
              return (
                <article
                  key={id || idx}
                  className={`event-card ${idx === 0 ? "event-card--highlight" : ""}`}
                  style={{ backgroundImage: `url(${item.image || item.cover || item.banner || item.poster || ""})`, cursor: isMobile ? 'pointer' : 'default' }}
                  onClick={() => { if (isMobile) navigate(`/myevents/${id}`); }}
                >
                  <div className="event-card__overlay" />
                  <div className="event-card__content">
                    <h3 className="event-card__title">{item.name}</h3>
                    <div className="event-card__meta">
                      <div className="event-card__meta-item">
                        <img src={calendario} alt="Ícono de calendario" style={{ width: 16, height: 16 }} />  
                        <span>{dateStr}</span>
                      </div>
                      <div className="event-card__meta-item">
                        <img src={ubi} alt="Ícono de ubicación" style={{ width: 16, height: 16 }} />
                        <span>{item.fulladdress || item.venue || item.location}</span>
                      </div>
                      <div className="event-card__meta-item">
                        <img src={boleto} alt="Ícono de entradas" style={{ width: 16, height: 16 }} />
                        <span>{entries} {entries === 1 ? "entrada" : "entradas"}</span>
                      </div>
                    </div>
                    <div className="event-card__actions">
                      {!isMobile && (
                        <button className="event-card__btn" onClick={() => navigate(`/myevents/${id}`)}>Ver mis tickets</button>
                      )}
                      {isSoon && <span className="event-card__badge">¡Próximo evento!</span>}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default MyEvents;
